/**
 * TELEMETRIA-S1 — recorder de execution traces no frontend.
 *
 * Singleton em memória que acumula eventos em buffer e flusha
 * pra POST /api/telemetry/execution. Tres triggers de flush:
 *   1. Buffer atinge BATCH_SIZE (20 eventos)
 *   2. Timer periódico de FLUSH_INTERVAL_MS (5s)
 *   3. beforeunload via sendBeacon (sobrevive ao close de aba)
 *
 * Princípios:
 *   - NÃO quebra UX: record() é fire-and-forget, sem await na chamada.
 *     Tudo internamente em try/catch silencioso.
 *   - Opt-out: localStorage.getItem('nexo_telemetry_enabled') === 'false'
 *     desliga TUDO (record vira no-op).
 *   - Auth: usa authFetch (Bearer token automático). Se o token não
 *     estiver presente, ingest retorna 401 e o caller silencia.
 *   - text NUNCA plain — hashText() retorna sha256[:16].
 *
 * API pública:
 *   - trace.record(event_type, payload, opts)
 *   - trace.flush()  → manual flush (await para testes)
 *   - hashText(s)    → sha256 prefix 16 hex chars
 *   - setTelemetryEnabled(bool) → toggle, atualiza localStorage
 *   - isTelemetryEnabled() → estado atual
 */
// Relative path (em vez de @/lib/authFetch) porque o jest do CRA não
// resolve alias jsconfig (só webpack faz). Mantém o smoke importável.
import { authFetch } from "../lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const ENDPOINT = `${API_URL}/api/telemetry/execution`;

const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5000;
const STORAGE_KEY = "nexo_telemetry_enabled";

const APP_VERSION = process.env.REACT_APP_GIT_SHA || "";

function _readEnabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

/**
 * sha256[:16] hex via Web Crypto. Retorna "" se SubtleCrypto não estiver
 * disponível (contexto não-seguro, browsers antigos) — sem crash.
 */
export async function hashText(text) {
  if (!text) return "";
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return "";
    const buf = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 16);
  } catch {
    return "";
  }
}

class ExecutionTraceRecorder {
  constructor() {
    this.enabled = _readEnabled();
    this.buffer = [];
    this.sessionId = "";  // setado externamente via setSessionId
    this.timer = null;
    this._startTimer();
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this._beaconFlush());
    }
  }

  setSessionId(sid) {
    this.sessionId = sid || "";
  }

  setEnabled(enabled) {
    this.enabled = !!enabled;
    try {
      localStorage.setItem(STORAGE_KEY, String(this.enabled));
    } catch {/* private mode */}
    if (!this.enabled) {
      this.buffer = [];
    }
  }

  /**
   * Record 1 evento. event_type é a chave canônica (ver
   * backend/models/execution_trace.py KNOWN_EVENT_TYPES).
   * Fire-and-forget — nada de await aqui.
   */
  record(eventType, payload = {}, opts = {}) {
    if (!this.enabled) return;
    try {
      const doc = {
        ts: new Date().toISOString(),
        session_id: opts.session_id || this.sessionId || "",
        event_type: eventType,
        source: "frontend",
        // env é overridado server-side, mas o model exige enum
        // ("dev" | "staging" | "prod"); manda "dev" como placeholder
        // (server ignora e sobreescreve com process.env APP_ENV).
        env: "dev",
        app_version: APP_VERSION,
        payload: payload || {},
      };
      if (opts.request_id) doc.request_id = opts.request_id;
      this.buffer.push(doc);
      if (this.buffer.length >= BATCH_SIZE) {
        // Não await — dispatch e segue. Falha silenciosa.
        this.flush().catch(() => {});
      }
    } catch (e) {
      // record nunca deve throw.
      // eslint-disable-next-line no-console
      console.debug("[trace] record failed (swallowed):", e);
    }
  }

  async flush() {
    if (!this.enabled) {
      this.buffer = [];
      return;
    }
    if (this.buffer.length === 0) return;
    const events = this.buffer.splice(0, this.buffer.length);
    try {
      const resp = await authFetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events }),
      });
      if (!resp.ok) {
        // 401 / 429 / 500 — silencioso, telemetry é soft.
        // Não re-buffera pra evitar memory leak em sessões offline.
        // eslint-disable-next-line no-console
        console.debug("[trace] flush HTTP", resp.status);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("[trace] flush failed (swallowed):", e);
    }
  }

  _beaconFlush() {
    if (!this.enabled || this.buffer.length === 0) return;
    try {
      if (typeof navigator === "undefined" || !navigator.sendBeacon) return;
      const events = this.buffer.splice(0, this.buffer.length);
      const blob = new Blob([JSON.stringify({ events })], {
        type: "application/json",
      });
      // sendBeacon ignora auth headers; o backend retornará 401 e
      // o batch será perdido. Em prática, sessions ativas sempre
      // têm token. Se um dia precisar, mover pro keepalive fetch.
      navigator.sendBeacon(ENDPOINT, blob);
    } catch {
      /* silent */
    }
  }

  _startTimer() {
    if (this.timer) return;
    this.timer = setInterval(() => {
      this.flush().catch(() => {});
    }, FLUSH_INTERVAL_MS);
  }

  // Test helper — reseta state interno entre cenários.
  _resetForTests() {
    this.buffer = [];
    this.enabled = _readEnabled();
    this.sessionId = "";
  }
}

const trace = new ExecutionTraceRecorder();

export function setTelemetryEnabled(b) {
  trace.setEnabled(b);
}

export function isTelemetryEnabled() {
  return trace.enabled;
}

export default trace;
